import { faAngleLeft } from '@fortawesome/free-solid-svg-icons/faAngleLeft';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons/faAngleRight';
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons/faExclamationCircle';
import isUndefined from 'lodash/isUndefined';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import i18n, { AppLangs } from '../i18n';
import { Breakpoints } from '../theme/Breakpoints';
import { FontSize } from '../theme/FontSize';
import { Rounded } from '../theme/Rounded';
import { Spacing } from '../theme/Spacing';
import { Tabs, TabsProps } from '../utils/local-antd';
import { AtiraIcon } from './AtiraIcon';
import { Button } from './Button';
import { Flex } from './Flex';

const StyledTabs = styled(Tabs)`
  padding-inline: ${Spacing.m};
  width: 100%;
  flex: 1;

  .ant-tabs-nav::before {
    display: none;
  }

  .ant-tabs-tabpane,
  .ant-tabs-content {
    height: 100%;
  }

  .ant-tabs-tabpane:focus-visible {
    outline: 0;
  }

  .ant-tabs-nav-wrap {
    height: fit-content;
    @media (max-width: ${Breakpoints.DESKTOP}) {
      width: 0;
    }
  }

  .ant-tabs-tab {
    color: ${({ theme }) => theme.fgPrimary} !important;
    cursor: pointer;
    outline: none;
    font-size: ${FontSize.m};
    border-radius: 0;
    display: inline;
    margin: 0 !important;

    &:first-child {
      padding: 0 ${Spacing.s};
    }

    &:not(:first-child) {
      padding-inline-end: ${Spacing.s};
      padding-block: 0;
    }

    &:not(:nth-last-child(1)):not(:nth-last-child(2)) {
      border-inline-end: ${({ theme }) => `1px solid ${theme.fgPrimary}`};
    }
  }

  .ant-tabs-tab-btn {
    transition: all 0.2s !important;
    user-select: none;
    font-size: ${FontSize.m};
    line-height: normal !important;
  }

  .ant-tabs-tab-btn:active,
  .ant-tabs-tab-btn:focus:not(:focus-visible),
  .ant-tabs-tab:not(.ant-tabs-tab-active):hover {
    color: ${({ theme }) => theme.main} !important;
  }

  .ant-tabs-ink-bar {
    display: none !important;
  }

  .ant-tabs-nav .ant-tabs-nav-wrap::before {
    box-shadow: none !important;
  }

  .ant-tabs-nav-list {
    gap: ${Spacing.s};
    font-size: ${FontSize.m};
    background-color: ${({ theme }) => theme.bgCanvas};
    width: fit-content;
    list-style-type: none;
    border-radius: ${Rounded.lg};
    overflow: hidden;
    padding: ${Spacing.s};
    margin-block: ${Spacing.m};
    overflow-x: auto;
    margin-top: ${Spacing.l};
  }

  .ant-tabs-nav-list::after,
  .ant-tabs-nav .ant-tabs-nav-wrap::after {
    display: none !important;
  }

  .ant-tabs-nav {
    gap: ${Spacing.s};
    border: 0 !important;
  }

  .ant-tabs-tab.ant-tabs-tab-active {
    color: ${({ theme }) => theme.main} !important;
  }

  .ant-tabs-tab-active:hover,
  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${({ theme }) => theme.main} !important;
  }

  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    color: inherit !important;
  }

  .ant-tabs-nav-operations {
    display: none !important;
  }

  .ant-tabs-content-holder {
    margin-bottom: ${Spacing.m};
  }
`;

const ButtonsWrapper = styled(Flex)`
  justify-content: flex-end;
  gap: ${Spacing.s};
  margin-bottom: ${Spacing.xxxl};
  padding-inline: ${Spacing.m};
`;

const StyledButton = styled(Button)`
  &:hover {
    background-color: ${({ theme }) => theme.mainHover};
  }
`;

const NextButton = styled(StyledButton)`
  & > div {
    flex-direction: row-reverse;
  }
`;

const ErrorSpan = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.s};
`;

interface AtiraTabsProps extends TabsProps {
  items: TabsProps['items'];
  className?: string;
  middleComponent?: React.ReactNode;
  sideComponent?: React.ReactNode;
  tabErrors?: Record<string, boolean>;
  showNavigationButtons?: boolean;
}

export const AtiraTabs: React.FC<AtiraTabsProps> = ({
  className,
  items: _items,
  sideComponent,
  middleComponent,
  tabErrors = {},
  activeKey,
  onChange,
  showNavigationButtons = false,
  ...props
}) => {
  const items = _items?.map((item) => {
    const hasError = tabErrors[item.key as string];
    if (!hasError) {
      return item;
    }

    return {
      ...item,
      label: (
        <ErrorSpan>
          {item.label}
          <AtiraIcon icon={faExclamationCircle} color="danger" />
        </ErrorSpan>
      ),
    };
  });

  const location = useLocation();
  const { t } = useTranslation();
  const targetedTab = location?.state?.targetedTab;

  const [activeTab, setActiveTab] = useState(targetedTab || items?.[0].key);

  const activeTabIndex = useMemo(
    () => items?.findIndex((item) => item.key === activeTab),
    [activeTab, items],
  );

  const isThereNextTab = useMemo(
    () =>
      !isUndefined(activeTabIndex) &&
      activeTabIndex >= 0 &&
      activeTabIndex < (items?.length || 0) - 1,
    [activeTabIndex, items?.length],
  );

  const isTherePreviousTab = useMemo(
    () => !isUndefined(activeTabIndex) && activeTabIndex > 0,
    [activeTabIndex],
  );

  const toNextTab = () => {
    const container = document.getElementById('outlet-wrapper');

    if (isTherePreviousTab) {
      setActiveTab(items![activeTabIndex! - 1].key!);

      if (container) {
        container.scrollTop = 0;
      }
    }
  };

  const toPreviousTab = () => {
    const container = document.getElementById('outlet-wrapper');

    if (isThereNextTab) {
      setActiveTab(items![activeTabIndex! + 1].key);

      if (container) {
        container.scrollTop = 0;
      }
    }
  };

  return (
    <Flex flexDirection="column" flex={1} height={'100%'}>
      <StyledTabs
        tabBarExtraContent={sideComponent}
        className={className}
        items={items}
        onChange={setActiveTab}
        activeKey={activeTab}
        {...props}
        animated
      />

      {showNavigationButtons ? (
        <ButtonsWrapper>
          <Button
            icon={i18n.language === AppLangs.AR ? faAngleRight : faAngleLeft}
            disabled={!isTherePreviousTab}
            onClick={toNextTab}
            hover
          >
            {t('common.back')}
          </Button>

          <NextButton
            icon={i18n.language === AppLangs.AR ? faAngleLeft : faAngleRight}
            disabled={!isThereNextTab}
            onClick={toPreviousTab}
          >
            {t('common.next')}
          </NextButton>
        </ButtonsWrapper>
      ) : null}
    </Flex>
  );
};
